import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { loadBrandFont, applyCustomCss } from '../lib/utils'

export interface BrandingState {
  primaryColor?: string
  secondaryColor?: string
  accentColor?: string
  backgroundColor?: string
  sidebarColor?: string
  textColor?: string
  logoUrl?: string
  faviconUrl?: string
  appName?: string
  fontFamily?: string
  borderRadius?: string
  customCss?: string
  loginBannerUrl?: string
}

export interface CompanyRef {
  companyId: string
  membershipId: string
  companyName: string
  companySlug: string
  isAdmin: boolean
  logoUrl?: string | null
  primaryColor?: string | null
}

/** A rental customer's client view: closed while "only during the event" keeps it shut (opensAt: when it opens) */
export interface ClientRef {
  clientPersonId: string; companyId: string; companyName: string; companySlug: string; customerName: string
  logoUrl?: string | null; primaryColor?: string | null; closed: boolean; opensAt: string | null
  /** the company's zone, for when it opens */
  timezone?: string
}

/** Where this sign-in can go: the companies it can enter, its client views, and Platform administration (global admins) */
export interface Destinations { platformAdmin: boolean; companies: CompanyRef[]; clients?: ClientRef[] }
const NO_DESTINATIONS: Destinations = { platformAdmin: false, companies: [], clients: [] }

/** Where a sign-in starts: its client view; its company; the console for a global admin without companies; else the landing page */
export function startPath(s: { activeCompanyId: string | null; clientPersonId?: string | null; destinations: Destinations }): string {
  if (s.clientPersonId) return '/portal'
  if (s.activeCompanyId) return '/dashboard'
  if (s.destinations.platformAdmin && s.destinations.companies.length === 0) return '/admin'
  return '/choose'
}

interface AuthState {
  /** the short-lived access token; the refresh token lives in a cookie this code cannot read */
  accessToken: string | null
  identityId: string | null
  email: string | null
  name: string | null
  isGlobalAdmin: boolean
  isSuperAdmin: boolean
  activeCompanyId: string | null
  membershipId: string | null
  /** a rental customer's client person: the client view (the portal) of activeCompanyId, no membership */
  clientPersonId: string | null
  isCompanyAdmin: boolean
  permissions: string[]
  /** the membership sees every site of the company */
  allSites: boolean
  branding: BrandingState
  /** the active company as the topbar shows it: its name, or its banner logo instead */
  company: { name: string; bannerUrl: string | null } | null
  /** the sign-in is tied to one company (its own address, or its single sign-on): no switching, no console */
  bound: boolean
  destinations: Destinations
  /** in Platform administration: global-admin rights, in no company */
  inConsole: () => boolean

  // Actions
  setAuth: (data: {
    accessToken: string
    identityId: string
    email: string
    name: string
    isGlobalAdmin?: boolean
    isSuperAdmin?: boolean
    activeCompanyId?: string | null
    membershipId?: string | null
    clientPersonId?: string | null
    isCompanyAdmin?: boolean
    permissions?: string[]
    bound?: boolean
    destinations?: Destinations
  }) => void
  setDestinations: (d: Destinations & { bound: boolean }) => void
  setBranding: (branding: Partial<BrandingState>) => void
  /** after moving the session: a company, or null for Platform administration */
  setActiveCompany: (data: { accessToken: string; activeCompanyId: string | null; membershipId: string | null; clientPersonId?: string | null }) => void
  /** a new session for the same person and company (after a password change ended every session) */
  setTokens: (tokens: { accessToken: string }) => void
  licenseModules: LicenseModules
  hasModule: (module: string) => boolean
  licenseValid: boolean
  hasPermission: (permission: string) => boolean
  fetchBranding: (companyId: string) => Promise<void>
  refreshMe: () => Promise<void>
  logout: () => void
}

export interface LicenseModules {
  webhooks: boolean; branding: boolean; apiKeys: boolean
  templates: boolean; customDomain: boolean; customEmail: boolean; sso: boolean; ownWhatsapp: boolean; ownRcs: boolean; linkedWhatsapp: boolean
  unifi: boolean; teltonika: boolean; peplink: boolean; mikrotik: boolean; velanox: boolean; omada: boolean; ruckus: boolean; starlink: boolean
}
// what the API assumes for a licence without a plan
const DEFAULT_MODULES: LicenseModules = {
  webhooks: true, branding: true, apiKeys: true, templates: true, customDomain: false, customEmail: false, sso: false, ownWhatsapp: false, ownRcs: false, linkedWhatsapp: false,
  unifi: true, teltonika: true, peplink: true, mikrotik: true, velanox: true, omada: true, ruckus: true, starlink: true,
}

const DEFAULT_BRANDING: BrandingState = {
  primaryColor: '',
  secondaryColor: '',
  accentColor: '',
  backgroundColor: '',
  sidebarColor: '',
  textColor: '',
  fontFamily: '',
  borderRadius: '',
  appName: 'Velanox',
  logoUrl: '',
  faviconUrl: '',
  loginBannerUrl: '',
  customCss: '',
}

function applyBrandingToDom(branding: BrandingState) {
  const r = document.documentElement.style

  // ALWAYS clear any previously set inline background/text overrides
  // so the data-theme CSS rules can take effect
  r.removeProperty('--bg')
  r.removeProperty('--bg-2')
  r.removeProperty('--bg-3')
  r.removeProperty('--bg-4')
  r.removeProperty('--fg')
  r.removeProperty('--fg-2')
  r.removeProperty('--fg-3')
  r.removeProperty('--line')
  r.removeProperty('--line-2')

  // Colours a company never picked stay empty and follow the theme. Values
  // equal to the old schema defaults (dark-theme colours) count as "not picked".
  const LEGACY = ['#3B82F6', '#06B6D4', '#F59E0B', '#0A0F1E', '#0F1629', '#F1F5F9']
  const picked = (c?: string | null): c is string => !!c && /^#[0-9a-f]{6}$/i.test(c.trim()) && !LEGACY.includes(c.trim().toUpperCase())
  const brightness = (c: string) => {
    const h = c.replace('#', '')
    return (parseInt(h.slice(0, 2), 16) * 299 + parseInt(h.slice(2, 4), 16) * 587 + parseInt(h.slice(4, 6), 16) * 114) / 1000
  }

  if (picked(branding.primaryColor)) {
    r.setProperty('--accent', branding.primaryColor)
    r.setProperty('--color-primary', branding.primaryColor)
    r.setProperty('--accent-soft', branding.primaryColor + '1e')
    r.setProperty('--accent-line', branding.primaryColor + '59')
  } else {
    for (const v of ['--accent', '--color-primary', '--accent-soft', '--accent-line']) r.removeProperty(v)
  }

  // Background and text only as a readable pair: a custom background without a
  // matching text colour (or vice versa) would break one of the two themes.
  r.removeProperty('--color-bg'); r.removeProperty('--color-text'); r.removeProperty('--color-sidebar')
  if (picked(branding.backgroundColor) && picked(branding.textColor)
      && Math.abs(brightness(branding.backgroundColor) - brightness(branding.textColor)) >= 125) {
    r.setProperty('--bg', branding.backgroundColor)
    r.setProperty('--color-bg', branding.backgroundColor)
    r.setProperty('--fg', branding.textColor)
    r.setProperty('--color-text', branding.textColor)
    if (picked(branding.sidebarColor) && Math.abs(brightness(branding.sidebarColor) - brightness(branding.textColor)) >= 125) {
      r.setProperty('--bg-2', branding.sidebarColor)
      r.setProperty('--color-sidebar', branding.sidebarColor)
    }
  }

  const font = loadBrandFont(branding.fontFamily)
  if (font) {
    // with fallbacks: a bare family name leaves the browser its default (serif) while the font loads
    r.setProperty('--font-body', `'${font}', system-ui, sans-serif`)
    r.setProperty('--font-display', `'${font}', system-ui, sans-serif`)
  } else {
    r.removeProperty('--font-body')
    r.removeProperty('--font-display')
  }

  if (branding.borderRadius && branding.borderRadius.trim()) {
    r.setProperty('--radius', branding.borderRadius)
  } else {
    r.removeProperty('--radius')
  }

  // Custom CSS (from company branding) — but strip any background/color overrides
  // that would fight the theme system
  applyCustomCss('dynamic-branding-css', branding.customCss)

  // The page has no icon link of its own, so the company's favicon was never shown
  let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (branding.faviconUrl) {
    if (!icon) { icon = document.createElement('link'); icon.rel = 'icon'; document.head.appendChild(icon) }
    icon.href = branding.faviconUrl
  } else icon?.remove()
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      identityId: null,
      email: null,
      name: null,
      isGlobalAdmin: false,
      isSuperAdmin: false,
      activeCompanyId: null,
      membershipId: null,
      clientPersonId: null,
      isCompanyAdmin: false,
      permissions: [],
      allSites: false,
      branding: DEFAULT_BRANDING,
      company: null,
      bound: false,
      destinations: NO_DESTINATIONS,

      inConsole: () => { const s = get(); return s.isGlobalAdmin && !s.activeCompanyId && !s.bound },

      setAuth: (data) => {
        localStorage.setItem('accessToken', data.accessToken)
        set({
          accessToken: data.accessToken,
          identityId: data.identityId,
          email: data.email,
          name: data.name,
          isGlobalAdmin: data.isGlobalAdmin ?? false,
          isSuperAdmin: data.isSuperAdmin ?? false,
          activeCompanyId: data.activeCompanyId ?? null,
          membershipId: data.membershipId ?? null,
          clientPersonId: data.clientPersonId ?? null,
          isCompanyAdmin: data.isCompanyAdmin ?? false,
          permissions: data.permissions ?? [],
          allSites: false,
          bound: data.bound ?? false,
          destinations: data.destinations ?? NO_DESTINATIONS,
        })
      },

      setDestinations: ({ bound, ...destinations }) => set({ bound, destinations }),

      setActiveCompany: (data) => {
        localStorage.setItem('accessToken', data.accessToken)
        // the console has the platform's own look, not the last company's
        if (!data.activeCompanyId) applyBrandingToDom(DEFAULT_BRANDING)
        set({
          ...(data.activeCompanyId ? {} : { branding: DEFAULT_BRANDING }),
          accessToken: data.accessToken,
          activeCompanyId: data.activeCompanyId,
          membershipId: data.membershipId,
          clientPersonId: data.clientPersonId ?? null,
          // nothing of the previous company's rights until /auth/me has answered for this one
          isCompanyAdmin: false, permissions: [], allSites: false, company: null,
          licenseModules: DEFAULT_MODULES,
        })
      },

      setTokens: ({ accessToken }) => {
        localStorage.setItem('accessToken', accessToken)
        set({ accessToken })
      },

      setBranding: (branding) => {
        const merged = { ...DEFAULT_BRANDING, ...branding }
        applyBrandingToDom(merged)
        set({ branding: merged })
      },

      // Fetch branding from API and apply — called after login to get fresh company branding
      fetchBranding: async (companyId: string) => {
        try {
          const { companiesApi } = await import('../lib/api')
          // a client person (the portal) reads the company's public look, as its login page does
          const { clientPersonId, destinations } = get()
          let slug = clientPersonId ? destinations.clients?.find(c => c.clientPersonId === clientPersonId)?.companySlug : undefined
          if (clientPersonId && !slug) slug = (await (await import('../lib/rentalApi')).portalApi.me()).company.slug
          const res = slug ? await companiesApi.publicBranding(slug) : await companiesApi.branding(companyId)
          const b = res.data?.data
          if (b) {
            const merged = { ...DEFAULT_BRANDING, ...b }
            applyBrandingToDom(merged)
            set({ branding: merged })
          }
        } catch { /* non-fatal */ }
      },

      licenseModules: DEFAULT_MODULES,
      licenseValid: true,

      hasModule: (module: string) => {
        const m = get().licenseModules as unknown as Record<string, boolean>
        return m[module] !== false
      },

      refreshMe: async () => {
        try {
          const { authApi } = await import('../lib/api')
          const res = await authApi.me()
          const m = res.data?.data
          if (!m) return
          set({
            isCompanyAdmin: m.isAdmin ?? false,
            permissions: m.permissions ?? [],
            allSites: m.allSites ?? false,
            licenseModules: m.licenseModules ?? DEFAULT_MODULES,
            licenseValid: m.licenseValid ?? true,
            company: m.company ? { name: m.company.name, bannerUrl: m.company.bannerUrl ?? null } : null,
          })
        } catch { /* silently ignore — stale token will be caught elsewhere */ }
      },

      hasPermission: (permission: string) => {
        const { inConsole, isCompanyAdmin, permissions } = get()
        if (inConsole() || isCompanyAdmin) return true
        return permissions.includes(permission)
      },

      logout: () => {
        localStorage.removeItem('accessToken')
        applyBrandingToDom(DEFAULT_BRANDING)
        set({
          licenseModules: DEFAULT_MODULES,
          licenseValid: true,
          accessToken: null,
          identityId: null, email: null, name: null,
          isGlobalAdmin: false, isSuperAdmin: false,
          activeCompanyId: null, membershipId: null, clientPersonId: null,
          isCompanyAdmin: false, permissions: [], allSites: false,
          branding: DEFAULT_BRANDING, company: null, bound: false, destinations: NO_DESTINATIONS,
        })
      },
    }),
    {
      name: 'velanox-auth',
      partialize: (s) => ({
        accessToken: s.accessToken,
        identityId: s.identityId,
        email: s.email,
        name: s.name,
        isGlobalAdmin: s.isGlobalAdmin,
        isSuperAdmin: s.isSuperAdmin,
        activeCompanyId: s.activeCompanyId,
        membershipId: s.membershipId,
        clientPersonId: s.clientPersonId,
        isCompanyAdmin: s.isCompanyAdmin,
        permissions: s.permissions,
        allSites: s.allSites,
        branding: s.branding,
        bound: s.bound,
        destinations: s.destinations,
      }),
    }
  )
)
